// Browser-side Trello service
// Uses proxiedFetch for CORS-safe API calls from the browser
// Trello uses a REST API at https://api.trello.com/1/

import { proxiedFetch } from "./cors-proxy";
import type {
  TrelloIntegrationSettings,
  TrelloBoard,
  TrelloCard,
  TrelloList,
  TrelloActivityEvent,
} from "./types";

interface TrelloConfig {
  apiKey: string;
  token: string;
}

export class BrowserTrelloService {
  private apiKey: string;
  private token: string;

  constructor(config: TrelloConfig) {
    this.apiKey = config.apiKey.trim();
    this.token = config.token.trim();
  }

  private buildUrl(path: string, extra?: Record<string, string>): string {
    const params = new URLSearchParams({
      key: this.apiKey,
      token: this.token,
      ...extra,
    });
    return `https://api.trello.com/1${path}?${params.toString()}`;
  }

  private async request<T>(path: string, extra?: Record<string, string>): Promise<T> {
    const url = this.buildUrl(path, extra);
    const res = await proxiedFetch(url, {
      headers: { Accept: "application/json" },
    });

    if (!res.ok) {
      throw new Error(`Trello API error ${res.status}: ${res.statusText}`);
    }

    return res.json() as Promise<T>;
  }

  async getMe(): Promise<{ id: string; fullName: string; username: string; avatarUrl?: string }> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const data = await this.request<any>("/members/me", { fields: "id,fullName,username,avatarUrl" });
    return {
      id: data.id,
      fullName: data.fullName,
      username: data.username,
      avatarUrl: data.avatarUrl
        ? `https://trello-members.s3.amazonaws.com/${data.id}/${data.avatarUrl}/170.png`
        : undefined,
    };
  }

  async getBoards(): Promise<TrelloBoard[]> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const data = await this.request<any[]>("/members/me/boards", {
      fields: "id,name,desc,closed,url,shortUrl,prefs",
      filter: "open",
    });

    return data.map((b) => ({
      id: b.id,
      name: b.name,
      desc: b.desc || "",
      closed: b.closed || false,
      url: b.url,
      shortUrl: b.shortUrl,
      prefs: b.prefs
        ? { background: b.prefs.background, backgroundColor: b.prefs.backgroundColor }
        : undefined,
    }));
  }

  async getBoardLists(boardId: string): Promise<TrelloList[]> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const data = await this.request<any[]>(`/boards/${boardId}/lists`, {
      filter: "open",
      fields: "id,name,closed,pos",
    });

    return data.map((l) => ({
      id: l.id,
      name: l.name,
      closed: l.closed || false,
      pos: l.pos,
    }));
  }

  async getBoardCards(
    boardId: string,
    opts: { limit?: number } = {}
  ): Promise<TrelloCard[]> {
    const limit = opts.limit || 100;

    // Get lists for name resolution
    const lists = await this.getBoardLists(boardId);
    const listMap = new Map(lists.map((l) => [l.id, l.name]));

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const data = await this.request<any[]>(`/boards/${boardId}/cards`, {
      fields: "id,name,desc,closed,url,shortUrl,idList,idMembers,labels,due,dueComplete,dateLastActivity",
      limit: String(limit),
    });

    return data.map((c) => ({
      id: c.id,
      name: c.name,
      desc: c.desc || "",
      closed: c.closed || false,
      url: c.url,
      shortUrl: c.shortUrl,
      idList: c.idList,
      listName: listMap.get(c.idList),
      idMembers: c.idMembers || [],
      labels: (c.labels || []).map((l: { id: string; name: string; color: string }) => ({
        id: l.id,
        name: l.name,
        color: l.color,
      })),
      due: c.due || undefined,
      dueComplete: c.dueComplete || false,
      dateLastActivity: c.dateLastActivity,
    }));
  }

  async getRecentActivity(
    boardId?: string,
    lookbackDays: number = 7
  ): Promise<TrelloActivityEvent[]> {
    const since = new Date(Date.now() - lookbackDays * 86400000).toISOString();

    if (!boardId) {
      // Fetch from member activity
      return this.getMemberActivity(lookbackDays);
    }

    return this.getBoardActivity(boardId, lookbackDays, since);
  }

  private async getMemberActivity(lookbackDays: number): Promise<TrelloActivityEvent[]> {
    const since = new Date(Date.now() - lookbackDays * 86400000).toISOString();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const data = await this.request<any[]>("/members/me/actions", {
      limit: "100",
      since,
      filter: "updateCard,createCard,commentCard,addMemberToCard,removeMemberFromCard,moveCardToBoard,moveCardFromBoard",
    });

    return this.parseActions(data);
  }

  private async getBoardActivity(
    boardId: string,
    _lookbackDays: number,
    since: string
  ): Promise<TrelloActivityEvent[]> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const data = await this.request<any[]>(`/boards/${boardId}/actions`, {
      limit: "100",
      since,
      filter: "updateCard,createCard,commentCard,addMemberToCard,removeMemberFromCard",
    });

    return this.parseActions(data);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private parseActions(actions: any[]): TrelloActivityEvent[] {
    return actions.map((action) => {
      const card = action.data?.card;
      const board = action.data?.board;
      const listBefore = action.data?.listBefore;
      const listAfter = action.data?.listAfter;
      const old = action.data?.old;

      let type = action.type || "unknown";
      let fieldName: string | undefined;
      let fromValue: string | undefined;
      let toValue: string | undefined;

      if (type === "updateCard" && listBefore && listAfter) {
        type = "moveCard";
      } else if (type === "updateCard" && old) {
        fieldName = Object.keys(old)[0];
        fromValue = typeof old[fieldName!] === "string" ? old[fieldName!] : JSON.stringify(old[fieldName!]);
        toValue = action.data?.card?.[fieldName!];
        if (typeof toValue !== "string") toValue = JSON.stringify(toValue);
      }

      return {
        id: action.id,
        timestamp: action.date,
        type,
        cardName: card?.name || "Unknown Card",
        boardName: board?.name || "Unknown Board",
        author: {
          displayName: action.memberCreator?.fullName || action.memberCreator?.username || "Unknown",
          avatarUrl: action.memberCreator?.avatarUrl
            ? `https://trello-members.s3.amazonaws.com/${action.memberCreator.id}/${action.memberCreator.avatarUrl}/170.png`
            : undefined,
        },
        listBefore: listBefore?.name,
        listAfter: listAfter?.name,
        fieldName,
        fromValue,
        toValue,
      };
    });
  }
}

// ==================== Validation ====================

export async function validateTrelloCredentials(
  settings: Pick<TrelloIntegrationSettings, "apiKey" | "token">
): Promise<{ valid: boolean; error?: string; user?: { id: string; fullName: string; username: string } }> {
  try {
    const svc = new BrowserTrelloService({ apiKey: settings.apiKey, token: settings.token });
    const me = await svc.getMe();
    return { valid: true, user: me };
  } catch (err: unknown) {
    const e = err as Error;
    return { valid: false, error: e.message || "Trello validation failed" };
  }
}
